<template>
  <v-form ref="vehicleForm" @submit="submitForm" v-model="isValidForm" v-focusNextOnEnter>
    <v-stepper-content :step="step" class="py-0 px-10">
      <v-divider class="divider mb-8" />
      <v-row no-gutters>
        <v-col cols="12">
          <span class="title-data-bike">
            {{ $t('honda.registration.vehicle.section_1.subtitle') }}
          </span>
        </v-col>

        <v-col cols="12">
          <m-form-row :label="`${$t('honda.registration.vehicle.section_1.lbl_offer_number')}`">
            <m-text-field
              :errorMessages="offerResultErrorMsg"
              :loading="offerNumberLoading"
              :placeholder="$t('honda.registration.vehicle.section_1.placeholder_offer_number')"
              :rules="offerRules"
              :successMessages="
                offerResultSuccessMsg ? $t('honda.registration.vehicle.section_1.lbl_offer_number_success') : ''
              "
              autofocus
              data-cy="registrationOfferNumber"
              maxLength="7"
              type="number"
              v-model="offerNumber" />
          </m-form-row>

          <m-form-row :label="`${$t('honda.registration.vehicle.section_1.input_1_title')}*`">
            <m-text-field
              data-cy="registrationVehicleVin"
              v-model="vehicle.vin.value"
              :placeholder="$t('honda.registration.vehicle.section_1.input_1_placeholder')"
              @input="handleVINInput"
              :rules="vehicle.vin.rules"
              maxlength="17"
              :errorMessages="vinValidationError"
              :successMessages="vinValidationSuccess"
              :disabled="forcedVin || offerNumberLoading"
              :loading="vinLoading"
              hideDetails="auto"
              :key="refreshModelKey" />
          </m-form-row>

          <m-form-row :label="`${$t('honda.registration.vehicle.section_1.input_2_title')}*`">
            <m-registration-number-field
              data-cy="registrationVehicleRegistrationNumber"
              v-model="vehicle.registrationNumber.value"
              :rules="vehicle.registrationNumber.rules"
              :readonly="exDealerBike != null"
              :country="country" />
          </m-form-row>

          <m-form-row :label="`${$t('honda.registration.vehicle.section_1.input_3_title')}*`">
            <m-select
              data-cy="registrationVehicleModelName"
              :placeholder="$t('honda.registration.vehicle.section_1.input_3_placeholder')"
              :value="vehicle.model.value"
              @input="setModel"
              itemText="description"
              :items="models"
              :rules="vehicle.model.rules"
              :disabled="fixedModel || offerNumberLoading || isModelsLoading || vinLoading"
              :attach="false"
              return-object
              :errorMessages="offerModelErrorMsg"
              :successMessages="offerModelSuccessMsg"
              :loading="isModelsLoading" />
          </m-form-row>

          <m-form-row :label="`${$t('honda.registration.vehicle.section_1.input_5_title')}`">
            <mapit-input
              uid="registrationVehicleKey"
              :value="vehicle.key.value"
              :placeholder="$t('honda.registration.vehicle.section_1.input_5_placeholder')"
              @changeValue="handleVehicleKeyChangeValue"
              :rules="vehicle.key.rules" />
          </m-form-row>
        </v-col>
      </v-row>

      <v-row no-gutters v-if="permissions.supportsSubagent">
        <v-col cols="12">
          <v-divider class="divider my-4" />
        </v-col>
        <v-col cols="12">
          <span class="title-data-bike">
            {{ $t('honda.registration.vehicle.section_2.subtitle') }}
          </span>
        </v-col>
        <v-col cols="12">
          <m-form-row :label="$t('honda.registration.vehicle.section_2.subtitle-2')">
            <mapit-input
              uid="registrationIsSubAgent"
              :value="subagent.exist"
              align="left"
              :type="InputTypes.RADIO_BUTTONS"
              :listItems="yesOrNoList"
              @changeValue="handleSubagentChangeValue"
              :rules="subagentRules" />
            <mapit-input
              uid="registrationSubagentName"
              ref="registrationSubagentName"
              class="pt-4"
              :type="InputTypes.TEXT_FIELD_WITH_SUGGESTIONS"
              :value="subagent.name"
              :placeholder="$t('offer-manager.create.step-offer-data.lbl-subagent-name')"
              :listItems="suggestions"
              @changeValue="handleSubagentNameChangeValue"
              :maxlength="100"
              :disabled="!subagent.exist" />
          </m-form-row>
        </v-col>
      </v-row>
    </v-stepper-content>

    <footer-actions :step="step">
      <template v-slot:left>
        <v-btn data-cy="registration-vehicle-button-back" class="btn-gray" @click="backStep()" dark tabindex="-1">
          {{ $t('buttons.back') }}
        </v-btn>
      </template>
      <template v-slot:right>
        <v-btn
          data-cy="registration-vehicle-button-continue"
          ref="nextStepBtn"
          class="btn-primary"
          :key="buttonKey"
          @click.once="checkValidVehicle()"
          :loading="loading"
          :disabled="!isValidForm || vinLoading || offerNumberLoading">
          {{ $t('buttons.next') }}
        </v-btn>
      </template>
    </footer-actions>
  </v-form>
</template>

<script lang="ts">
import {OfferType, Status} from '@muvio/honda-offers-builder/model'
import {computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch} from '@vue/composition-api'
import {debounce, head} from 'lodash'
import moment from 'moment'

import {DealerBikesApi} from '@/api/dealerBikes.api'
import offerApi from '@/api/offers.api'
import {vinValidation} from '@/api/vehicle.api'
import {NUM_CHARACTERS_VALID_VIN} from '@/api/vehicle.api.js'
import MapitInput from '@/components/general/MapitInput.vue'
import {useModels} from '@/composables'
import {toCapitalize} from '@/helpers/utils'
import {isAlphanumeric, isNumberEvent} from '@/helpers/validations'
import i18n from '@/i18n'
import {ModelWithDescription} from '@/types'
import {DealerBikeStatus} from '@/types/dealer-bikes'
import {Predelivery} from '@/types/dynamodb-types'
import {InputTypes, RegistrationTypes} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'
import {createModelName} from '@/utils/utils'
import {accountRegistrationModel, subagentRegistrationModel, vehicleRegistrationModel} from '@/vuex/models'
import {closeAlert, showAlert} from '@/vuex/modules/ui'
import {SET_EX_DEALER_BIKE} from '@/vuex/mutation-types'

import FooterActions from '../FooterActions.vue'

export default defineComponent({
  name: 'Vehicle',
  components: {FooterActions, MapitInput},
  props: {
    step: {
      type: Number,
      required: true,
    },
  },
  setup(props, {root, refs, emit}) {
    const vinValidationError = ref<string>()
    const vinValidationSuccess = ref<string>()
    const vinLoading = ref(false)
    const offerNumberLoading = ref(false)
    const vehicle = ref(vehicleRegistrationModel())
    const subagent = ref(subagentRegistrationModel())
    const fixedModel = ref(false)
    const loading = ref(false)
    const buttonKey = ref(1)
    const forcedVin = ref(false)
    const offerResultSuccessMsg = ref(false)
    const offerResultErrorMsg = ref('')
    const offerModelSuccessMsg = ref('')
    const offerModelErrorMsg = ref('')
    const refreshKey = ref(0)
    const isValidForm = ref(false)
    const yesOrNoList = ref([
      {text: i18n.t(`buttons.yes`), value: true},
      {text: i18n.t(`buttons.no`), value: false},
    ])
    const refreshModelKey = ref(0)
    const abortController = ref(new AbortController())
    const predelivery = ref<Predelivery>()

    const debounceOfferSearch = debounce(handleOfferNumberChange, 100)
    const debounceCheckVinValidation = debounce(checkVinValidation, 100)
    // Identifies the latest VIN check, so a superseded one does not end the loading state of the current one
    let vinCheckId = 0

    const {
      getBranch: branch,
      getCountry: country,
      getExDealerBike: exDealerBike,
      getForcedType: forcedType,
      getGroup: group,
      getModelsList: modelsList,
      getOffer: offer,
      getOfferNumber: getOfferNumber,
      getOfferNumberValue: offerNumberValue,
      getPermissions: permissions,
      getSubagentSuggestion: suggestions,
    } = mapGetters(root.$store.getters)

    const subagentRules = computed(() => {
      return [(e: any) => e !== undefined || root.$t('honda.registration.vehicle.validations.subagent').toString()]
    })

    const isForcedHondaPlus = computed(() => {
      return forcedType.value === RegistrationTypes.HONDA_PLUS
    })

    const isCreatedFromOffer = computed(() => {
      return forcedType.value === RegistrationTypes.OFFER_REGISTRATION
    })

    const offerNumber = computed({
      get: () => offerNumberValue.value,
      set: value => {
        root.$store.commit('changeOfferNumber', value)
      },
    })

    const offerRules = computed(() => {
      return offerNumber.value.length === 0 ? [] : getOfferNumber.value.rules
    })

    const {
      models,
      loading: isModelsLoading,
      getSummaryByIdMaterial,
    } = useModels({
      branch: branch.value,
    })

    onMounted(() => {
      if (root.$route.query.vin) {
        vehicle.value.vin.value = root.$route.query.vin as string
        forcedVin.value = true
      }

      if (!permissions.value.supportsSubagent) {
        // @ts-ignore
        subagent.value.exist = false
      }

      if (root.$route.query.forcedType !== RegistrationTypes.HONDA_MAPIT_TRANSFERRED) {
        showAlert(root.$store.dispatch, 'info', 'honda.registration.offer-code-reminder', 15000)
      }
    })

    onBeforeUnmount(() => {
      closeAlert(root.$store.dispatch)
    })

    watch(offerNumber, value => {
      debounceOfferSearch(value)
    })

    watch(
      () => vehicle.value.vin.value,
      val => {
        vinCheckId++
        if (vinLoading.value) {
          abortController.value.abort()
          abortController.value = new AbortController()
          vinLoading.value = false
        }

        vinValidationError.value = ''
        vinValidationSuccess.value = ''
        vehicle.value.vin.status = null
        if (val.length === NUM_CHARACTERS_VALID_VIN) {
          const isAlphanum = isAlphanumeric(val)
          if (typeof isAlphanum !== 'string') {
            //si tenemos un "texto" de error
            // Loading from now on, not after the debounce, so the dealer cannot continue in between
            vinLoading.value = true
            debounceCheckVinValidation()
          } else {
            vinValidationError.value = isAlphanum
          }
        }
        if (fixedModel.value) {
          fixedModel.value = false
          vehicle.value.model.value = ''
          vehicle.value.modelId = null
        }
      },
    )

    watch(
      () => subagent.value.exist,
      val => {
        if (!val) {
          subagent.value.name = ''
          nextTick(() => {
            // @ts-ignore
            refs.nextStepBtn.$el.focus()
          })
        } else {
          nextTick(() => {
            // @ts-ignore
            refs.registrationSubagentName.$el.getElementsByTagName('input')[0].focus()
          })
        }
      },
    )

    watch(
      () => offer.value.vehicleModelName,
      val => {
        nextTick(() => {
          if (val && models.value && !fixedModel.value) {
            const exactModel = models.value.find(model => model.description === val)
            if (exactModel) {
              vehicle.value.model.value = exactModel.description
            }
          }
        })
      },
    )

    watch(offer, val => {
      nextTick(() => {
        // @ts-ignore
        subagent.value.exist = val.type === OfferType.subagent
        subagent.value.name = val.subagentName || ''
        refreshKey.value++
      })
    })

    function handleVehicleKeyChangeValue(value: string) {
      vehicle.value.key.value = value.toUpperCase()
    }

    function handleSubagentChangeValue(value: any) {
      subagent.value.exist = value
    }

    function handleSubagentNameChangeValue(value: string) {
      subagent.value.name = toCapitalize(value)
    }

    function handleVINInput(value: string) {
      vehicle.value.vin.value = value.toUpperCase()
    }

    function backStep() {
      emit('backStep')
    }

    function updateAccountFromOffer() {
      const account = accountRegistrationModel()

      account.email.value = offer.value.clientEmail || ''
      account.firstName.value = offer.value.clientName || ''
      account.lastName.value = offer.value.clientSurname || ''
      account.phone.value = offer.value.clientPhone || ''
      account.dob.value = offer.value.clientDob || ''
      account.address.addressRegion.value = offer.value.clientProvince || ''

      root.$store.dispatch('addAccount', account)
    }

    async function handleOfferNumberChange(value: string) {
      offerNumberLoading.value = true
      offerResultErrorMsg.value = ''
      offerResultSuccessMsg.value = false

      root.$store.dispatch('changeOfferNumber', value)

      if (value.length < 6) {
        offerNumberLoading.value = false
        return
      }

      try {
        const offerCode = `${group.value.code}-${value}`
        const response = await offerApi.getOfferByCode(offerCode, group.value.dealerId)

        const offerFromCode = response.data
        const {status, predelivery: predeliveryData} = offerFromCode

        if (isOfferInvalid(status)) {
          offerNumberLoading.value = false
          offerResultSuccessMsg.value = false
          offerResultErrorMsg.value = root
            .$t('honda.registration.vehicle.section_1.lbl_offer_number_error_refused', {
              status: root.$t(`offer-manager.create.step-offer-status.lbl-offer-status-${status}`),
            })
            .toString()

          return
        }

        root.$store.dispatch('initNewOffer', offerFromCode)
        root.$store.dispatch('setForcedType', RegistrationTypes.OFFER_REGISTRATION)

        updateAccountFromOffer()

        if (predeliveryData) {
          predelivery.value = predeliveryData

          if (predelivery.value?.vehicle?.vin) {
            vehicle.value.vin.value = predelivery.value.vehicle.vin
          } else {
            const materialId = predelivery.value?.vehicle?.idMat
            setVehicleMaterialId(materialId)

            const summary = await getSummaryByIdMaterial(predelivery.value?.vehicle?.idMat)

            if (summary) {
              await setVehicleModel(summary?.hpConfig?.HPG?.available || false, createModelName(summary.model) ?? '')
            } else {
              offerModelSuccessMsg.value = ''
              offerModelErrorMsg.value = root
                .$t('honda.registration.vehicle.section_1.lbl_offer_number_error_model')
                .toString()
            }
          }
        }

        offerResultSuccessMsg.value = true
        offerResultErrorMsg.value = ''
      } catch (error) {
        console.error(error)
        offerResultSuccessMsg.value = false
        offerResultErrorMsg.value = root.$t('honda.registration.vehicle.section_1.lbl_offer_number_error').toString()
      }

      offerNumberLoading.value = false
    }

    function submitForm(e: Event) {
      checkValidVehicle()
      e.preventDefault()
    }

    function isOfferInvalid(status: Status) {
      return [Status.Pending, Status.Refused].includes(status)
    }

    async function checkValidVehicle() {
      // The VIN check resolves the model asynchronously; continuing before it ends would send an empty model
      if (vinLoading.value || offerNumberLoading.value) {
        buttonKey.value++
        return
      }

      if (!loading.value) {
        loading.value = true
        const validExDealerBikeRegistrationNumber = exDealerBike.value
          ? true
          : await isRegistrationNumberFree(vehicle.value.registrationNumber.value)
        if (
          isValidForm.value &&
          !vinLoading.value &&
          vinValidationError.value === '' &&
          validExDealerBikeRegistrationNumber &&
          subagent.value.exist !== undefined
        ) {
          vehicle.value.registrationNumber.value = vehicle.value.registrationNumber.value.toUpperCase()
          vehicle.value.vin.value = vehicle.value.vin.value.toUpperCase()
          root.$store
            .dispatch('addVehicle', vehicle.value)
            .then(() => {
              root.$store
                .dispatch('addSubagent', subagent.value)
                .then(() => nextStep())
                .catch(err => console.log('Error add vehicle', err))
                .finally(() => {
                  loading.value = false
                  buttonKey.value++
                })
            })
            .catch(err => {
              loading.value = false
              buttonKey.value++
              console.log('Error add vehicle', err)
            })
        } else {
          loading.value = false
          buttonKey.value++
        }
      }
    }
    function nextStep() {
      closeAlert(root.$store.dispatch)
      emit('nextStep')
    }

    async function setupRegistrationUsingVin(vin: string) {
      const dealerBikesApi = new DealerBikesApi()
      const [maybeExDealerBike, vinCheckResult] = await Promise.all([
        dealerBikesApi
          .getDealerBikeByVin(vin, abortController.value.signal)
          .then(res => head(res.data.data.filter(db => db.status === DealerBikeStatus.Retired)))
          .catch(() => undefined),
        vinValidation(vin, abortController.value.signal, {
          skipEligibilityCheck: forcedVin.value,
        }),
      ])
      root.$store.commit(SET_EX_DEALER_BIKE, maybeExDealerBike)
      return vinCheckResult
    }

    async function setVehicleModel(hasHondaPlusGo: boolean, modelName: string) {
      await root.$store.dispatch('setHasGoModel', hasHondaPlusGo)
      const exactModel = models.value.find(model => model.description === modelName)
      const forcedModel = {
        name: modelName,
        description: modelName,
        category: '',
        cc: '',
        group: '',
        type: '',
        kw: '',
      } as ModelWithDescription
      if (!exactModel) {
        models.value.push(forcedModel)
      }

      vehicle.value.model.value = exactModel?.description || forcedModel.description
      fixedModel.value = true
      offerModelSuccessMsg.value = ''
      offerModelErrorMsg.value = ''
    }

    function setVehicleMaterialId(materialId?: string) {
      //@ts-ignore modelId is a JS file with a null string value
      vehicle.value.modelId = materialId || null
    }

    async function checkVinValidation() {
      const checkId = ++vinCheckId
      vinLoading.value = true

      try {
        await validateVin()
      } finally {
        // Also on errors: a stuck loading state would keep the dealer from continuing
        if (checkId === vinCheckId) {
          vinLoading.value = false
          nextTick(() => {
            refreshModelKey.value++
            // @ts-ignore
            refs.vehicleForm.validate()
          })
        }
      }
    }

    async function validateVin() {
      const result = await setupRegistrationUsingVin(vehicle.value.vin.value)

      vehicle.value.vin.status = result.key

      if (result.model) {
        let hasHondaPlusGo = false
        if (result.idMat) {
          setVehicleMaterialId(result.idMat)

          const summary = await getSummaryByIdMaterial(result.idMat, abortController.value.signal)
          hasHondaPlusGo = summary?.hpConfig?.HPG?.available || false
        }

        await setVehicleModel(hasHondaPlusGo, result.model)
      } else if (predelivery.value) {
        const materialId = predelivery.value.vehicle.idMat
        setVehicleMaterialId(materialId)

        const summary = await getSummaryByIdMaterial(materialId)

        if (summary) {
          await setVehicleModel(summary?.hpConfig?.HPG?.available || false, createModelName(summary.model) ?? '')
        } else {
          vehicle.value.model.value = ''
          vehicle.value.modelId = null
          offerModelSuccessMsg.value = ''
          offerModelErrorMsg.value = root
            .$t('honda.registration.vehicle.section_1.lbl_offer_number_error_model')
            .toString()
        }
      }

      if (exDealerBike.value) {
        vehicle.value.registrationNumber.value = exDealerBike.value.vehicle.registrationNumber
      }
      if (result.error) {
        vinValidationError.value = i18n.t(result.status).toString()
      } else if (
        vehicle.value.vin.status !== 'NO_VIN_BDD' &&
        exDealerBike.value &&
        moment().diff(exDealerBike.value.vehicle.registrationDate, 'years') > 0
      ) {
        if (isForcedHondaPlus.value) {
          vinValidationError.value = i18n
            .t('honda.registration.vehicle.validations.vin_invalid_ex_demo_more_one_year')
            .toString()
        } else {
          vinValidationSuccess.value = i18n.t('honda.registration.vehicle.validations.vin_invalid_ex_demo').toString()
        }
      } else {
        if (vehicle.value.vin.status === 'NO_VIN_BDD') {
          if (!forcedVin.value) {
            vinValidationError.value = exDealerBike.value
              ? i18n.t('honda.registration.vehicle.validations.vin_invalid_ex_demo').toString()
              : i18n.t(result.status).toString()
          }
        } else {
          vinValidationSuccess.value = exDealerBike.value
            ? i18n.t('honda.registration.vehicle.validations.vin_valid_ex_demo').toString()
            : i18n.t(result.status).toString()
        }
      }
    }

    function isNumber(evt: Event) {
      return isNumberEvent(evt)
    }

    function setModel(model: ModelWithDescription) {
      vehicle.value.model.value = model.description
      offerModelErrorMsg.value = ''
    }

    async function isRegistrationNumberFree(registrationNumber: string) {
      try {
        const bikeWithPlate = await root.$store.dispatch('loadVehicleByRegistrationNumber', registrationNumber)
        if (bikeWithPlate.data.data.length > 0) {
          root.$store.dispatch('setAlertResult', {
            type: 'error',
            stringId: 'honda.registration.vehicle.validations.registration_number_used',
            open: true,
          })
          return false
        } else {
          return true
        }
      } catch (err) {
        console.error('Error isRegistrationNumberFree:', err)
        return true
      }
    }

    return {
      backStep,
      branch,
      buttonKey,
      checkValidVehicle,
      country,
      debounceCheckVinValidation,
      exDealerBike,
      fixedModel,
      forcedVin,
      handleSubagentChangeValue,
      handleSubagentNameChangeValue,
      handleVehicleKeyChangeValue,
      handleVINInput,
      InputTypes,
      isCreatedFromOffer,
      isForcedHondaPlus,
      isModelsLoading,
      isNumber,
      isRegistrationNumberFree,
      isValidForm,
      loading,
      models,
      modelsList,
      nextStep,
      offer,
      offerModelErrorMsg,
      offerModelSuccessMsg,
      offerNumber,
      offerNumberLoading,
      offerResultErrorMsg,
      offerResultSuccessMsg,
      offerRules,
      permissions,
      refreshKey,
      refreshModelKey,
      setModel,
      subagent,
      subagentRules,
      submitForm,
      suggestions,
      toCapitalize,
      vehicle,
      vinLoading,
      vinValidationError,
      vinValidationSuccess,
      yesOrNoList,
    }
  },
})
</script>

<style lang="scss">
.divider {
  border-color: var(--v-gray-base) !important;
}

.title-data-bike {
  text-align: left;
  color: var(--v-primary-base);
  font-size: 24px !important;
  text-transform: uppercase;
  font-family: 'ProximaNovaExCnSbold', sans-serif;
}

.text-field-vehicle {
  margin: 10px 0;

  label {
    margin: -5px 0;
  }
}

.text-area-vehicle {
  margin: 10px 0;

  label {
    margin: -10px 0;
  }
}

#isSubagent {
  .v-item--active {
    .fa-dot-circle {
      color: var(--v-gray-darken4) !important;
      caret-color: var(--v-gray-darken4) !important;
    }
  }
}

.hide-arrow {
  .v-input__append-inner {
    display: none;
  }
}
</style>
